/**
 * DynamoWrapper Element Matchers For CMS & Commerce Data Source Definitions
 *
 * This file defines utilities that should be used to build `matchElement`
 * properties of List Binding Context Definitions for Collection Lists in
 * the CMS and Ecommerce data source definitions.
 */

import {Plugins, Component} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';

import {
  getCollectionIdFromElement,
  getCollectionItemRefSetSlugFromElement,
  getContextCollectionIdFromElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

import type {ElementMatcher} from '@packages/systems/dynamo/source-types';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';

const dynamoWrapperType = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoWrapper')
);

function matchDynamoWrapper(
  matcher: (arg1: ExpressionElement) => boolean
): ElementMatcher {
  return {elementType: dynamoWrapperType, matcher};
}

export const DynamoWrapperElementMatchers = {
  // builds a generic Collection List matchElement definition
  // using the `matcher` function provided as the argument
  create: matchDynamoWrapper,

  // builds a matchElement definition for a Collection List connected to
  // an ItemRefSet field
  itemRefSetList(
    // slug of the ItemRefSet field
    itemRefSetSlug: string,
    // id of the collection that is referenced by the ItemRefSet field
    targetCollectionId: string,
    // id of the collection that the ItemRefSet field belongs to
    contextCollectionId: string
  ): ElementMatcher {
    function matchMultiReferenceList(element: ExpressionElement): boolean {
      const elementItemRefSetSlug =
        getCollectionItemRefSetSlugFromElement(element);
      const elementTargetCollectionId = getCollectionIdFromElement(element);
      const elementContextCollectionId =
        getContextCollectionIdFromElement(element);

      return (
        // itemRefSetSlug must match dyn.collection.itemRefSet
        itemRefSetSlug === elementItemRefSetSlug &&
        // targetCollectionId must match dyn.collection.id
        targetCollectionId === elementTargetCollectionId && // contextCollectionId must match dyn.collection.contextCollectionId
        contextCollectionId === elementContextCollectionId
      );
    }

    return matchDynamoWrapper(matchMultiReferenceList);
  },
} as const;
